// ID_CREATED*************************

import React, { useState, useMemo, useCallback, memo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as reduxActions from "store/actions";
import reduxStore from "store/";

// COMPONENTS
import NewCard from "components/Card/new";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import ErrorBoundary from "components/ErrorBoundary";
import HtmlTooltip from "components/Tooltip/html";
import ButtonOptions from "componentsV3/Buttons";

// FUNCTIONS
import { translate, customApp, getDate } from "functions/";

// Configuração de ícones para diferentes tipos de canais
const CHANNEL_ICONS = {
    'rh': 'people',
    'ti': 'developer_mode',
    'financeiro': 'payments',
    'juridico': 'gavel',
    'suporte': 'support_agent',
    'manutencao': 'build',
    'marketing': 'campaign',
    'vendas': 'trending_up',
    'default': 'contact_support'
};

// Função para detectar ícone baseado no nome/descrição
const getChannelIcon = (channel) => {
    const text = `${channel.name} ${channel.shortCutTitle} ${channel.shortCutDescription}`.toLowerCase();

    for (const [key, icon] of Object.entries(CHANNEL_ICONS)) {
        if (key === 'default') continue;
        if (text.includes(key)) return icon;
    }
    return CHANNEL_ICONS.default;
};

// Componente Channel Card Moderno e Compacto
const ModernChannelCard = memo(({ channel, onChannelClick, index }) => {
    const [isHovered, setIsHovered] = useState(false);
    const icon = getChannelIcon(channel);

    const cardStyles = useMemo(() => ({
        container: {
            background: `linear-gradient(135deg, 
                ${customApp('menu')}15 0%, 
                ${customApp('menu')}05 50%, 
                transparent 100%)`,
            backdropFilter: "blur(10px)",
            border: `1px solid ${customApp('menu')}20`,
            borderRadius: "16px",
            padding: "20px",
            position: "relative",
            cursor: "pointer",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            overflow: "hidden",
            height: "120px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            animationDelay: `${index * 0.1}s`,
            animation: "slideInUp 0.6s ease-out forwards",
            opacity: 0,
            transform: isHovered ? "translateY(-8px) scale(1.02)" : "translateY(0) scale(1)",
            boxShadow: isHovered ?
                `0 20px 40px ${customApp('menu')}20, 0 0 0 1px ${customApp('menu')}30` :
                `0 8px 16px ${customApp('menu')}10`
        },
        iconContainer: {
            width: "60px",
            height: "60px",
            borderRadius: "16px",
            background: `linear-gradient(135deg, ${customApp('menu')}, ${customApp('menu')}dd)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 8px 16px ${customApp('menu')}30`,
            transition: "all 0.3s ease",
            transform: isHovered ? "scale(1.1) rotate(5deg)" : "scale(1) rotate(0deg)"
        },
        icon: {
            fontSize: "28px",
            color: "#ffffff",
            fontWeight: "500"
        },
        content: {
            flex: 1,
            minWidth: 0
        },
        title: {
            fontSize: "16px",
            fontWeight: "700",
            color: "#1a1a1a",
            marginBottom: "6px",
            lineHeight: "1.3",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
        },
        description: {
            fontSize: "13px",
            color: "#666",
            lineHeight: "1.4",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            marginBottom: "8px"
        },
        actionButton: {
            fontSize: "11px",
            fontWeight: "600",
            color: customApp('menu'),
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            opacity: 0.8,
            transition: "all 0.2s ease"
        },
        glowEffect: {
            position: "absolute",
            top: "-50%",
            left: "-50%",
            width: "200%",
            height: "200%",
            background: `radial-gradient(circle, ${customApp('menu')}10 0%, transparent 70%)`,
            opacity: isHovered ? 1 : 0,
            transition: "opacity 0.3s ease",
            pointerEvents: "none"
        }
    }), [isHovered, index]);

    return (
        <>
            <style jsx>{`
                @keyframes slideInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
            <div
                style={cardStyles.container}
                onClick={() => onChannelClick(channel)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div style={cardStyles.glowEffect} />

                <div style={cardStyles.iconContainer}>
                    <span className="material-icons" style={cardStyles.icon}>
                        {icon}
                    </span>
                </div>

                <div style={cardStyles.content}>
                    <HtmlTooltip
                        arrow
                        title={channel.shortCutTitle}
                        placement="top"
                    >
                        <div style={cardStyles.title}>
                            {channel.shortCutTitle || channel.name}
                        </div>
                    </HtmlTooltip>

                    <div style={cardStyles.description}>
                        {channel.shortCutDescription}
                    </div>

                    <div style={cardStyles.actionButton}>
                        {channel.shortCutButtonToAdd || "ABRIR CANAL"}
                    </div>
                </div>
            </div>
        </>
    );
});

// Componente de Request Card Futurístico
const FuturisticRequestCard = memo(({ request, index, onRequestClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    const getStatusInfo = useCallback((status) => {
        const statusLower = status?.toLowerCase() || '';
        switch (statusLower) {
            case 'completed':
            case 'finalizado':
            case 'concluído':
                return {
                    color: '#00e676',
                    bgColor: '#00e67610',
                    label: 'Finalizado',
                    icon: 'check_circle',
                    pulse: false
                };
            case 'in progress':
            case 'em progresso':
            case 'andamento':
                return {
                    color: '#2196f3',
                    bgColor: '#2196f310',
                    label: 'Em Progresso',
                    icon: 'sync',
                    pulse: true
                };
            case 'pending':
            case 'pendente':
                return {
                    color: '#ff9800',
                    bgColor: '#ff980010',
                    label: 'Pendente',
                    icon: 'schedule',
                    pulse: true
                };
            case 'cancelled':
            case 'cancelado':
                return {
                    color: '#f44336',
                    bgColor: '#f4433610',
                    label: 'Cancelado',
                    icon: 'cancel',
                    pulse: false
                };
            default:
                return {
                    color: '#9e9e9e',
                    bgColor: '#9e9e9e10',
                    label: 'Novo',
                    icon: 'fiber_new',
                    pulse: true
                };
        }
    }, []);

    const statusInfo = getStatusInfo(request.status);

    const cardStyles = useMemo(() => ({
        container: {
            background: `linear-gradient(135deg, 
                ${statusInfo.bgColor} 0%, 
                rgba(255,255,255,0.95) 20%, 
                rgba(255,255,255,0.98) 100%)`,
            backdropFilter: "blur(20px)",
            border: `1px solid ${statusInfo.color}30`,
            borderLeft: `4px solid ${statusInfo.color}`,
            borderRadius: "16px",
            padding: "20px",
            marginBottom: "12px",
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            position: "relative",
            overflow: "hidden",
            transform: isHovered ? "translateX(8px)" : "translateX(0)",
            boxShadow: isHovered ?
                `0 16px 32px ${statusInfo.color}20, 0 0 0 1px ${statusInfo.color}20` :
                "0 4px 12px rgba(0,0,0,0.08)"
        },
        header: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "12px"
        },
        titleSection: {
            flex: 1,
            marginRight: "16px"
        },
        title: {
            fontSize: "15px",
            fontWeight: "700",
            color: "#1a1a1a",
            marginBottom: "4px",
            lineHeight: "1.3"
        },
        requestId: {
            fontSize: "11px",
            color: "#666",
            fontWeight: "500",
            fontFamily: "monospace",
            backgroundColor: "#f5f5f5",
            padding: "2px 6px",
            borderRadius: "4px",
            display: "inline-block"
        },
        statusBadge: {
            display: "flex",
            alignItems: "center",
            gap: "6px",
            backgroundColor: statusInfo.color,
            color: "#fff",
            padding: "6px 12px",
            borderRadius: "20px",
            fontSize: "11px",
            fontWeight: "600",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            boxShadow: `0 4px 12px ${statusInfo.color}40`,
            animation: statusInfo.pulse ? "pulse 2s infinite" : "none"
        },
        description: {
            fontSize: "13px",
            color: "#444",
            lineHeight: "1.5",
            marginBottom: "12px",
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        footer: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "11px",
            color: "#666",
            paddingTop: "12px",
            borderTop: "1px solid #f0f0f0"
        },
        progressBar: {
            position: "absolute",
            bottom: 0,
            left: 0,
            height: "3px",
            backgroundColor: statusInfo.color,
            borderRadius: "0 0 16px 16px",
            transition: "width 0.3s ease",
            width: isHovered ? "100%" :
                statusInfo.label === 'Finalizado' ? "100%" :
                    statusInfo.label === 'Em Progresso' ? "60%" :
                        statusInfo.label === 'Pendente' ? "30%" : "10%"
        }
    }), [statusInfo, isHovered]);

    const formatDate = useCallback((timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }, []);

    return (
        <>
            <style jsx>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.7; }
                }
            `}</style>
            <div
                style={cardStyles.container}
                onClick={() => onRequestClick && onRequestClick(request)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div style={cardStyles.header}>
                    <div style={cardStyles.titleSection}>
                        <div style={cardStyles.title}>
                            {request.name || request.title || `Solicitação #${index + 1}`}
                        </div>
                        <div style={cardStyles.requestId}>
                            #{request._id?.substring(0, 8)}
                        </div>
                    </div>
                    <div style={cardStyles.statusBadge}>
                        <span className="material-icons" style={{ fontSize: "12px" }}>
                            {statusInfo.icon}
                        </span>
                        {statusInfo.label}
                    </div>
                </div>

                {request.description && (
                    <div style={cardStyles.description}>
                        {request.description}
                    </div>
                )}

                <div style={cardStyles.footer}>
                    <span>📅 {getDate(request.created_at)}</span>
                    <span>👤 {request._createdBy?.name || 'Usuário'}</span>
                </div>

                <div style={cardStyles.progressBar} />
            </div>
        </>
    );
});

// Header moderno com glassmorphism
const ModernHeader = memo(({ title, showCards, onToggle, requestCount }) => {
    return (
        <div style={{
            background: `linear-gradient(135deg, 
                rgba(255,255,255,0.95) 0%, 
                rgba(255,255,255,0.8) 100%)`,
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            padding: "24px 32px",
            marginBottom: "32px",
            border: `1px solid ${customApp('menu')}20`,
            boxShadow: `0 8px 32px ${customApp('menu')}10`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
        }}>
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "16px"
            }}>
                <div style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    background: `linear-gradient(135deg, ${customApp('menu')}, ${customApp('menu')}dd)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 8px 16px ${customApp('menu')}30`
                }}>
                    <span className="material-icons" style={{
                        fontSize: "24px",
                        color: "#fff"
                    }}>
                        hub
                    </span>
                </div>
                <div>
                    <h2 style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: "#1a1a1a",
                        margin: 0,
                        letterSpacing: "-0.5px"
                    }}>
                        {translate(title)}
                    </h2>
                    <p style={{
                        fontSize: "14px",
                        color: "#666",
                        margin: 0,
                        marginTop: "4px"
                    }}>
                        {showCards ?
                            `${requestCount} solicitações encontradas` :
                            "Selecione um canal para criar solicitação"
                        }
                    </p>
                </div>
            </div>

            <div style={{
                display: "flex",
                gap: "8px",
                background: "rgba(255,255,255,0.5)",
                padding: "6px",
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.3)"
            }}>
                <ButtonOptions
                    text="📋 Canais"
                    active={!showCards}
                    onClick={() => onToggle(false)}
                    style={{
                        borderRadius: "8px",
                        padding: "10px 20px",
                        fontWeight: "600",
                        fontSize: "13px"
                    }}
                />
                <ButtonOptions
                    text={`📊 Minhas Solicitações (${requestCount})`}
                    active={showCards}
                    onClick={() => onToggle(true)}
                    style={{
                        borderRadius: "8px",
                        padding: "10px 20px",
                        fontWeight: "600",
                        fontSize: "13px"
                    }}
                />
            </div>
        </div>
    );
});

function MyChannels(props) {
    const { db, session } = reduxStore.getState();
    const [showCards, setShowCards] = useState(false);

    // Memoizar canais disponíveis
    const availableChannels = useMemo(() => {
        return Object.keys(db.cards || {})
            .filter(c => {
                const card = db.cards[c];
                return card
                    && !card.deleted
                    && card.type === "step"
                    && card.showInPanel
                    && card.showGroups?.some(group =>
                        db.users[session._id]?.groups?.some(userGroup =>
                            userGroup.value === group.value
                        )
                    );
            })
            .sort((a, b) => {
                const nameA = (db.cards[a].shortCutTitle || db.cards[a].name).toLowerCase().trim();
                const nameB = (db.cards[b].shortCutTitle || db.cards[b].name).toLowerCase().trim();
                return nameA.localeCompare(nameB);
            })
            .map(c => db.cards[c]);
    }, [db.cards, db.users, session._id]);

    // Memoizar solicitações do usuário
    const userRequests = useMemo(() => {
        return Object.keys(db.cards || {})
            .filter(card => {
                const cardData = db.cards[card];
                return cardData?._createdBy?.channel
                    && cardData?._createdBy?._id === session._id;
            })
            .sort((a, b) => {
                const dateA = db.cards[a].created_at || 0;
                const dateB = db.cards[b].created_at || 0;
                return dateB - dateA;
            })
            .map(card => db.cards[card]);
    }, [db.cards, session._id]);

    // Callbacks
    const handleChannelClick = useCallback((channel) => {
        console.log('Canal selecionado:', channel);
        props.reduxFunction("ASYNC", "SET_CONTROL", {
            ...reduxStore.getState().control,
            addCard: {
                _parent: channel._id,
                rows: 0
            },
        });
    }, [props]);

    // Função para abrir a solicitação no side module - IMPLEMENTAÇÃO ADICIONADA
    const handleRequestClick = useCallback((request) => {
        console.log('Solicitação selecionada:', request);

        // Abrir a atividade no side module usando a mesma estrutura do componente Card
        props.reduxFunction("ASYNC", "SET_MODULE", {
            ...reduxStore.getState().sideModule,
            db: db,
            id: request._id,
            module: "cardEdit",
            activeModule: "cardEdit", // Pode ser alterado para outros módulos como "timeline" se necessário
            data: request,
        });
    }, [props, db]);

    if (availableChannels.length === 0) {
        return <div />;
    }

    return (
        <ErrorBoundary>
            <div style={{
                width: "100%",
                minHeight: "400px",
                background: `linear-gradient(135deg, 
                    ${customApp('menu')}05 0%, 
                    transparent 50%, 
                    ${customApp('menu')}03 100%)`,
                borderRadius: "24px",
                padding: "24px",
                position: "relative",
                overflow: "hidden"
            }}>
                {/* Background Pattern */}
                <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `radial-gradient(circle at 20% 20%, ${customApp('menu')}08 0%, transparent 50%),
                                    radial-gradient(circle at 80% 80%, ${customApp('menu')}05 0%, transparent 50%)`,
                    pointerEvents: "none"
                }} />

                <GridContainer style={{ position: "relative", zIndex: 0 }}>
                    <ModernHeader
                        title="$__myChannels"
                        showCards={showCards}
                        onToggle={setShowCards}
                        requestCount={userRequests.length}
                    />

                    {showCards ? (
                        <GridItem xs={12}>
                            <div style={{
                                maxHeight: "600px",
                                overflowY: "auto",
                                paddingRight: "8px",
                                scrollbarWidth: "thin",
                                scrollbarColor: `${customApp('menu')}40 transparent`
                            }}>
                                {userRequests.length > 0 ? (
                                    userRequests.map((request, index) => (
                                        <FuturisticRequestCard
                                            key={`request-${request._id}-${index}`}
                                            request={request}
                                            index={index}
                                            onRequestClick={handleRequestClick}
                                        />
                                    ))
                                ) : (
                                    <div style={{
                                        textAlign: "center",
                                        padding: "80px 40px",
                                        color: "#666",
                                        background: "rgba(255,255,255,0.5)",
                                        borderRadius: "16px",
                                        backdropFilter: "blur(10px)"
                                    }}>
                                        <span className="material-icons" style={{
                                            fontSize: "64px",
                                            marginBottom: "16px",
                                            display: "block",
                                            opacity: 0.3,
                                            color: customApp('menu')
                                        }}>
                                            inbox
                                        </span>
                                        <h3 style={{
                                            fontSize: "18px",
                                            marginBottom: "8px",
                                            color: "#333"
                                        }}>
                                            Nenhuma solicitação encontrada
                                        </h3>
                                        <p style={{ fontSize: "14px", opacity: 0.7 }}>
                                            Suas solicitações aparecerão aqui quando você criar uma.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </GridItem>
                    ) : (
                        <GridItem xs={12}>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
                                gap: "16px",
                                marginTop: "8px"
                            }}>
                                {availableChannels.map((channel, index) => (
                                    <ModernChannelCard
                                        key={channel._id}
                                        channel={channel}
                                        index={index}
                                        onChannelClick={handleChannelClick}
                                    />
                                ))}
                            </div>
                        </GridItem>
                    )}
                </GridContainer>
            </div>
        </ErrorBoundary>
    );
}

const mapStateToProps = (store) => ({
    store: {
        cards: store.db.cards,
    },
    hidePrivate: store.controls.hidePrivate,
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(reduxActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(memo(MyChannels));